
import React, { KeyboardEventHandler, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from "react";
import { v4 as uuid } from 'uuid';
import { EventKey, KEY_MAPS } from "./_event.map";

export const last = (array: any[]) => {
    if (array.length === 0) return undefined;
    return array[array.length - 1];
}

export function resetOnosition<T>(array: Array<T>, item: T): T[] {
    const index = array.indexOf(item);
    if (index >= 0) {
        return array.slice(0, index)
    }
    return array;
}

export function useResetState<T>(value: T) {
    const resetState = (v?: T) => {
        v = v ?? value;
        setState(v);
    }
    const [state, setState] = useState(value);

    return [state, resetState];
}

// export type KeyboardEventFunction = (e: React.KeyboardEvent) => void | undefined;
// export type MouseEventFunction = (e: React.MouseEvent) => void | undefined;

export type onKeyupType = {
    onSelect?: KeyboardEventHandler;
    onArrowKeyDown?: KeyboardEventHandler;
    onArrowKeyUp?: KeyboardEventHandler;
    onArrowKeyRight?: KeyboardEventHandler;
    onArrowKeyLeft?: KeyboardEventHandler;
    onEscape?: KeyboardEventHandler;
}

/** keyUpWrapper to map some keyup values and map the handler function */
export const keyUpWrapper = ({ onSelect, onArrowKeyDown, onArrowKeyUp, onArrowKeyRight, onArrowKeyLeft, onEscape }: onKeyupType) => {
    /* eslint-disable indent */
    return (event: React.KeyboardEvent) => {
        switch (event.code) {
            case KEY_MAPS.Enter.code: onSelect && onSelect(event); break;
            // case KEY_MAPS.Space.code: onSelect && onSelect(event); break;
            case KEY_MAPS.ArrowUp.code: onArrowKeyUp && onArrowKeyUp(event); break;
            case KEY_MAPS.ArrowDown.code: onArrowKeyDown && onArrowKeyDown(event); break;
            case KEY_MAPS.ArrowRight.code: onArrowKeyRight && onArrowKeyRight(event); break;
            case KEY_MAPS.ArrowLeft.code: onArrowKeyLeft && onArrowKeyLeft(event); break;
            case KEY_MAPS.Escape.code: onEscape && onEscape(event); break;
        }
    }
}

const selectEventKey = [KEY_MAPS.Enter.code, KEY_MAPS.Space.code];
/** isSelectEvent is defined to detect the any key press from selectEventKey array */
export const isSelectEvent = (e: React.KeyboardEvent) => selectEventKey.includes(e.code);

const unSelectEventKey = [KEY_MAPS.Escape.code];
/** isEscape is defined to detect the any key press from unSelectEventKey[Escape] array */
export const isEscape = (e: React.KeyboardEvent) => unSelectEventKey.includes(e.code);

const navigationKey = [KEY_MAPS.ArrowUp.code, KEY_MAPS.ArrowDown.code, KEY_MAPS.Tab.code];

/** isNavigationEvent is defined to detect the any key press from navigationKey[Arrow up and down, right, left] array */
export const isNavigationEvent = (e: React.KeyboardEvent) => navigationKey.includes(e.code);
export const isNavigationEventHorizantal = (e: React.KeyboardEvent) => [KEY_MAPS.ArrowRight.code, KEY_MAPS.ArrowLeft.code].includes(e.code);
export const isNavigationEventVertical = (e: React.KeyboardEvent) => [KEY_MAPS.ArrowDown.code, KEY_MAPS.ArrowUp.code].includes(e.code);

/** isEvent check similarity between tow event */
export const isEvent = (e: React.KeyboardEvent, eventKey: EventKey) => e.code === eventKey.code;

/** classNames is defined to merge all the params send in the function and omit undefined and false or null or empty values */
export const classNames = (...params: any[]) => params.reduce((previous: string[], current: string): string[] => {
    if (typeof current === 'boolean' || (Boolean(current) === false && typeof current !== 'number') || current === undefined || current === null || current === '') return previous;
    return previous.concat(current);
}, []).join(' ');

/** creates an array with specified number */
export const createArray = (number: number) => {
    return Array.from(Array(number).keys()).map(k => k + 1)
}

export const useSkipEffectFirstTime = (callback: () => any | void, args: any[]) => {
    const firstUpdate = useRef(true);
    useLayoutEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        return callback();
    }, args ?? []);
}

export function useFocusTrapping(node: HTMLElement | null, dependes: any[] = []) {
    const tabbableElements = useRef<HTMLElement[]>([]);

    const onAction = (event: KeyboardEvent) => {
        if (node) {
            if (isEventTargetWithin(event, node) && tabbableElements.current.length > 0 && isNavigationEvent(event as any)) {
                const lastItem = tabbableElements.current[tabbableElements.current.length - 1];
                if (lastItem.isSameNode(document.activeElement)) {
                    setTimeout(() => {
                        tabbableElements.current[0].focus();
                    }, 0)
                }
            }
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', onAction);
        return () => {
            document.removeEventListener('keydown', onAction);
        }
    }, dependes);
}

export const useTextChangeState = (initialValue: any) => {
    const [state, setState] = useState(initialValue);

    const eventSetState = (event: any) => {
        setState(event.target.value);
    }
    return [state, eventSetState];
}

export const generateID = uuid;

export const setBodyOverflowHidden = (noScroll = false) => {
    if (noScroll === true) {
        document.body.classList.add('ted-ui-no-scroll');
    } else {
        document.body.classList.remove('ted-ui-no-scroll');
    }
}

//Devices break point
export const DEVICES_BREAK_POINT = {
    xs: 768,
    sm: 1024,
    md: 1680
}

export function isScrolledIntoView(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    const elemTop = rect.top;
    const elemBottom = rect.bottom;

    // Only completely visible elements return true:
    const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
    // Partially visible elements return true:
    //isVisible = elemTop < window.innerHeight && elemBottom >= 0;
    return isVisible;
}

export const immutable = (param: any): any => {
    if (Array.isArray(param)) {
        return param.map(item => immutable(item));
    }
    return JSON.parse(JSON.stringify(param));
}

export const getScrollableParents = (node: HTMLElement | null): HTMLElement[] => {
    const scrollableParents: HTMLElement[] = [];

    while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const isScrollable = /(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX);

        if (isScrollable) {
            scrollableParents.push(node);
        }

        node = node.parentNode as HTMLElement;
    }

    return scrollableParents;
}

export function isEventTargetWithin(
    event: Event,
    node: Node | null | undefined,
) {
    if (node == null) {
        return false;
    }

    if ('composedPath' in event) {
        return event.composedPath().includes(node);
    }

    // TS thinks `event` is of type never as it assumes all browsers support composedPath, but browsers without shadow dom don't
    const e = event as Event;
    return e.target != null && node.contains(e.target as Node);
}

/**
 * Returns back a NodeList of focusable elements
 * that exist within the passed parent HTMLElement, or
 * an empty array if no parent passed.
 *
 * @param {HTMLElement} parent HTML element
 * @returns {(NodeList|Array)} The focusable elements that we can find
 */
export const getAllFocusableElements = (parent: HTMLElement) => {
    if (!parent) {
        console.warn('You need to pass a parent HTMLElement');
        return []; // Return array so length queries will work
    }

    return parent.querySelectorAll(
        'button:not([disabled]), [href]:not(use), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
    );
};

export function domRect(x: number, y: number, width: number, height: number) {
    return { x, y, width, height, top: y, bottom: y + height, left: x, right: x + width } as unknown as DOMRect
}

export const ErrorComponent = ({ children }: PropsWithChildren) => {
    return <p className="ted-ui-internal-error"><b>Internal Error</b>: <span>{children}</span></p>
}

export const ReplaceMe = () => {
    return <div className='sb-replace-me'>
        (Replace Me)
    </div>
}


export const calculateResizeType = (width?: number): ResizeType => {
    width = width ?? window.innerWidth;

    if (width <= 767) return 'xs';
    if (width >= 768 && width <= 1023) return 'sm';
    if (width >= 1024 && width <= 1679) return 'md';
    if (width >= 1680) return 'lg';

    return 'xs';
}

type ResizeType = 'xs' | 'sm' | 'md' | 'lg';

export const useResize = (fn: (rz: ResizeType) => void, dependencies: any[] = []) => {
    dependencies = [window.innerHeight, window.innerWidth, ...dependencies];
    useLayoutEffect(() => {
        const onResize = () => {
            const y = calculateResizeType();
            if (fn) {
                fn(y);
            }
        }

        onResize();

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, dependencies);
};

export const getAbortController = (): { controller: AbortController, signal: { signal: AbortSignal } } => {
    const controller = new AbortController();
    const { signal } = controller;
    const abort = {
        controller, signal: { signal }
    };
    return abort;
}
export const consoleWith = (fn?: any) => {
    return (data: any) => {
        console.log(data);
        fn && fn(data);
    }
}
export const setClickActionButton = (fn: (event?: MouseEvent) => void) => ({ onClick: fn })
